import { z } from 'zod';
import {
  E164_REGEX,
  normalizeStoreContactPhone,
  STORE_CONTACT_DEFAULT_COUNTRY,
  type AdminOrderSellableVariant,
  type CreateAdminPreparedOrderInput,
  type FulfillmentSelection,
  type FulfillmentAcceptance,
  type StoreContactCountrySelectValue,
} from '@dukanti/shared';
import type { Locality } from '@dukanti/shared/types';

export interface PreparedOrderLine {
  readonly variant: AdminOrderSellableVariant;
  readonly quantity: number;
}

export interface PreparedOrderDeliveryDraft {
  readonly customerName: string;
  readonly phoneCountry: StoreContactCountrySelectValue;
  readonly phoneNumber: string;
  readonly localityId: string;
  readonly addressLine1: string;
  readonly customerNotes: string;
}

export type PreparedOrderValidationError =
  | 'fulfillment.unavailable'
  | 'fulfillment.optionsChanged'
  | 'admin.orders.prepare.errors.noLines'
  | 'admin.orders.prepare.errors.quantityInvalid'
  | 'admin.orders.prepare.errors.insufficientStock'
  | 'admin.orders.prepare.errors.customerNameRequired'
  | 'admin.orders.prepare.errors.phoneRequired'
  | 'admin.orders.prepare.errors.phoneInvalid'
  | 'admin.orders.prepare.errors.localityRequired'
  | 'admin.orders.prepare.errors.localityInvalid'
  | 'admin.orders.prepare.errors.addressTooLong';

const contactSchema = z.object({
  customerName: z.string().trim().max(200),
  phoneCountry: z.string().min(1),
  phoneNumber: z.string().trim().min(1),
  customerNotes: z.string().trim().max(500),
});

const fulfillmentDraftSchema = z.discriminatedUnion('method', [
  contactSchema.extend({
    method: z.literal('delivery'),
    localityId: z.string().trim().min(1),
    addressLine1: z.string().trim().max(300),
  }),
  contactSchema.extend({ method: z.literal('pickup'), pickupPlaceId: z.string().uuid() }),
]);

export function mergePreparedOrderLine(
  lines: readonly PreparedOrderLine[],
  variant: AdminOrderSellableVariant,
): PreparedOrderLine[] {
  const existing = lines.find((line) => line.variant.variant_id === variant.variant_id);
  if (!existing) return [...lines, { variant, quantity: 1 }];
  return lines.map((line) =>
    line.variant.variant_id === variant.variant_id
      ? { ...line, quantity: Math.min(line.quantity + 1, variant.available_stock) }
      : line,
  );
}

export function updatePreparedOrderLineQuantity(
  lines: readonly PreparedOrderLine[],
  variantId: string,
  quantity: number,
): PreparedOrderLine[] {
  return lines.map((line) =>
    line.variant.variant_id === variantId ? { ...line, quantity } : line,
  );
}

export function removePreparedOrderLine(
  lines: readonly PreparedOrderLine[],
  variantId: string,
): PreparedOrderLine[] {
  return lines.filter((line) => line.variant.variant_id !== variantId);
}

export function calculatePreparedOrderSubtotal(lines: readonly PreparedOrderLine[]): number {
  return lines.reduce(
    (total, line) =>
      total +
      (Number.isInteger(line.quantity) && line.quantity > 0 ? line.quantity : 0) *
        line.variant.effective_price_cents,
    0,
  );
}

export function validatePreparedOrderLines(
  lines: readonly PreparedOrderLine[],
): PreparedOrderValidationError | null {
  if (lines.length === 0) return 'admin.orders.prepare.errors.noLines';
  const invalidQuantity = lines.some(
    (line) => !Number.isInteger(line.quantity) || line.quantity <= 0,
  );
  if (invalidQuantity) return 'admin.orders.prepare.errors.quantityInvalid';
  const insufficientStock = lines.some((line) => line.variant.available_stock < line.quantity);
  return insufficientStock ? 'admin.orders.prepare.errors.insufficientStock' : null;
}

export function getPreparedOrderBlockingErrors(params: {
  readonly lines: readonly PreparedOrderLine[];
  readonly delivery: PreparedOrderDeliveryDraft;
  readonly localities: readonly Locality[];
  readonly fulfillment?: FulfillmentSelection;
}): PreparedOrderValidationError[] {
  const errors: PreparedOrderValidationError[] = [];
  const lineError = validatePreparedOrderLines(params.lines);
  if (lineError) errors.push(lineError);

  if (params.delivery.phoneNumber.trim() === '') {
    errors.push('admin.orders.prepare.errors.phoneRequired');
  } else {
    const phone = normalizePreparedOrderPhone(params.delivery);
    if (!phone.ok) errors.push(phone.error);
  }

  if (params.fulfillment?.method !== 'pickup' && params.delivery.localityId.trim() === '') {
    errors.push('admin.orders.prepare.errors.localityRequired');
  } else if (
    params.fulfillment?.method !== 'pickup' &&
    !params.localities.some((candidate) => candidate.id === params.delivery.localityId)
  ) {
    errors.push('admin.orders.prepare.errors.localityInvalid');
  }

  return errors;
}

export function normalizePreparedOrderPhone(
  draft: Pick<PreparedOrderDeliveryDraft, 'phoneCountry' | 'phoneNumber'>,
):
  | { readonly ok: true; readonly e164: string }
  | { readonly ok: false; readonly error: PreparedOrderValidationError } {
  if (draft.phoneNumber.trim() === '')
    return { ok: false, error: 'admin.orders.prepare.errors.phoneRequired' };
  const normalized = normalizeStoreContactPhone({
    country: draft.phoneCountry,
    localNumber: draft.phoneNumber,
  });
  if (!normalized.ok || !normalized.e164 || !E164_REGEX.test(normalized.e164)) {
    return { ok: false, error: 'admin.orders.prepare.errors.phoneInvalid' };
  }
  return { ok: true, e164: normalized.e164 };
}

export function makeDefaultPreparedOrderDeliveryDraft(): PreparedOrderDeliveryDraft {
  return {
    customerName: '',
    phoneCountry: STORE_CONTACT_DEFAULT_COUNTRY,
    phoneNumber: '',
    localityId: '',
    addressLine1: '',
    customerNotes: '',
  };
}

export function buildCreateAdminPreparedOrderInput(params: {
  readonly storeId: string;
  readonly lines: readonly PreparedOrderLine[];
  readonly delivery: PreparedOrderDeliveryDraft;
  readonly localities: readonly Locality[];
  readonly fulfillment: FulfillmentSelection;
  readonly acceptance: FulfillmentAcceptance;
}):
  | { readonly ok: true; readonly input: CreateAdminPreparedOrderInput }
  | { readonly ok: false; readonly error: PreparedOrderValidationError } {
  const blockingErrors = getPreparedOrderBlockingErrors(params);
  const firstError = blockingErrors[0];
  if (firstError) return { ok: false, error: firstError };

  const parsed = fulfillmentDraftSchema.safeParse({ ...params.delivery, ...params.fulfillment });
  if (!parsed.success) {
    const fieldNames = new Set(parsed.error.issues.map((issue) => String(issue.path[0] ?? '')));
    if (fieldNames.has('phoneNumber')) {
      return { ok: false, error: 'admin.orders.prepare.errors.phoneRequired' };
    }
    if (fieldNames.has('localityId')) {
      return { ok: false, error: 'admin.orders.prepare.errors.localityRequired' };
    }
    if (fieldNames.has('addressLine1')) {
      return { ok: false, error: 'admin.orders.prepare.errors.addressTooLong' };
    }
    return { ok: false, error: 'admin.orders.prepare.errors.customerNameRequired' };
  }

  const phone = normalizePreparedOrderPhone(params.delivery);
  if (!phone.ok) return { ok: false, error: phone.error };

  const base = {
    storeId: params.storeId,
    customerName: parsed.data.customerName,
    customerPhone: phone.e164,
    customerNotes: parsed.data.customerNotes || null,
    acceptance: params.acceptance,
    lines: params.lines.map((line) => ({
      variant_id: line.variant.variant_id,
      quantity: line.quantity,
    })),
  };
  if (
    params.fulfillment.method !== params.acceptance.selection.method ||
    params.fulfillment.pickupPlaceId !== params.acceptance.selection.pickupPlaceId
  ) {
    return { ok: false, error: 'fulfillment.optionsChanged' };
  }
  if (parsed.data.method === 'pickup') {
    return {
      ok: true,
      input: {
        ...base,
        fulfillment: { method: 'pickup', pickupPlaceId: parsed.data.pickupPlaceId },
      },
    };
  }
  const localityId = parsed.data.localityId;
  const locality = params.localities.find((candidate) => candidate.id === localityId);
  if (!locality) return { ok: false, error: 'admin.orders.prepare.errors.localityInvalid' };
  return {
    ok: true,
    input: {
      ...base,
      fulfillment: { method: 'delivery', pickupPlaceId: null },
      deliveryLocalityId: locality.id,
      addressLine1: parsed.data.addressLine1,
    },
  };
}
